import { Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Store } from '@ngrx/store';
import { filter, Subject, take } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';
import { FloatingInputComponent } from '@app/framework/inputs/floating-input/floating-input.component';
import { InputCalendarComponent } from '@app/framework/inputs/input-calendar/input-calendar.component';
import { primeContractStatuses } from '@app/store/prime-commitments/prime-commitments.constants';
import { PrimeChangeOrderGeneral } from '@app/store/prime-commitments/prime-commitments.types';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { pccoActions } from '@app/store/prime-commitments/prime-commitments.actions';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { isPersistedFile, ManagedFile } from '@app/framework/interfaces/file.type';
import { InputUploadComponent } from '@app/framework/file-handling/input-upload/input-upload.component';

@Component({
  selector: 'app-prime-contact-change-order-general-sidebar',
  imports: [
    DropdownComponent,
    FormsModule,
    CdkTextareaAutosize,
    FloatingInputComponent,
    InputCalendarComponent,
    AsyncPipe,
    InputUploadComponent,
  ],
  templateUrl: './prime-contact-change-order-general-sidebar.component.html',
  styleUrl: './prime-contact-change-order-general-sidebar.component.scss',
})
export class PrimeContactChangeOrderGeneralSidebarComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  @ViewChild('generalForm') generalForm: NgForm;

  @Input() model: PrimeChangeOrderGeneral = {
    id: null,
    title: '',
    number: null,
    status: null,
    date: null,
    description: '',
    files: [],
  };
  customIdDisabled = true;
  originalPCCONumber = null;

  protected readonly statuses = primeContractStatuses;

  nextPCCONumber$ = this.store.select(primeSelectors.selectNextPCCONumber);
  isPCCOValid$ = this.store.select(primeSelectors.selectIsPCCONumberValid);
  project$ = this.store.select(viewProjectSelectors.getSelectedProject);

  isDestroyed$ = new Subject<void>();

  ngOnInit() {
    if (!this.model.number) {
      this.setNextPPCONumber();
    } else {
      this.originalPCCONumber = this.model.number;
    }
    this.validateNumber();
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  validateNumber() {
    this.isPCCOValid$.pipe(skip(1), takeUntil(this.isDestroyed$)).subscribe((isValid) => {
      if (
        !this.generalForm?.form?.controls?.number ||
        this.generalForm?.form?.controls?.number?.value === ''
      ) {
        return;
      }

      if (isValid) {
        this.generalForm.form.controls.number.setErrors(null);
        return;
      }

      this.generalForm.form.controls.number.setErrors({ pattern: true });
    });
  }

  onCustomIdIconClick() {
    if (!this.customIdDisabled) {
      this.setOriginalPCCONumber();
    }

    this.customIdDisabled = !this.customIdDisabled;
  }

  setOriginalPCCONumber() {
    this.model.number = this.originalPCCONumber;
  }

  setNextPPCONumber() {
    this.nextPCCONumber$
      .pipe(
        filter((data) => !!data),
        take(1),
      )
      .subscribe((nextPCCONumber) => {
        this.model.number = nextPCCONumber;
      });
  }

  numberChanged(number: string) {
    this.store.dispatch(pccoActions.onPCCONumberChange({ number, resource_id: this.model.id }));
  }

  isValid() {
    this.generalForm.form.markAllAsTouched();
    return !!this.generalForm.form.valid;
  }

  protected filesChanged(files: ManagedFile[]) {
    this.model.files = files;
  }

  protected removeFile({ file, index }: { file: ManagedFile; index: number }) {
    if (isPersistedFile(file)) {
      this.model.files = this.model.files.map((modelFile) => {
        if (isPersistedFile(modelFile) && modelFile.id === file.id) {
          return {
            ...modelFile,
            toBeDeleted: true,
          };
        }
        return modelFile;
      });
    } else {
      this.model.files = this.model.files.filter((_, modelIndex) => modelIndex !== index);
    }
  }
}
